import React, { useState } from "react";
import { ArrowLeft, Download, FileQuestion, FileText, Image as ImageIcon, Loader2, RefreshCw, TriangleAlert } from "lucide-react";
import { toast } from "sonner";

import { GhostButton, IconButton } from "@/shared/components/ui-kit";
import { FileTypeIcon } from "@/shared/components/icons/filetype";
import { saveBlob } from "@/shared/components/viewer/files";
import { getKbNodeFile, useKbNodeContent, useReindexKbNode } from "@/shared/api/knowledge";
import { KbNodeMenu } from "./KbNodeMenu";
import { FileStatusBadge } from "./KbStatus";
import { KbIndexed } from "./KbIndexed";
import { Original, Raw } from "./PreviewBody";
import { extOf, formatSize } from "./format";
import { useInlineRename } from "./hooks/useInlineRename";
import { kindOf, TAB_HINT, TAB_LABEL, tabsFor } from "./preview";

import type { KbNode } from "@/shared/types";

/* A file opened beside the tree: who it is, then the three ways to read it — as uploaded,
   as extracted, as the agent gets it. Raw and Indexed share one fetch of the text. */
export function KbFileView({ kbId, node, where, onBack, onRename, onMove, onDelete }: {
  kbId: string;
  node: KbNode;
  /** The folder it sits in, or the base's name. */
  where?: string;
  /** On a phone: back to the folder. */
  onBack?: () => void;
  onRename?: (name: string) => void;
  onMove?: () => void;
  onDelete?: () => void;
}) {
  const file = node.file;
  const kind = kindOf(node);
  const content = useKbNodeContent(kbId, node.id, kind !== "image");
  const reindex = useReindexKbNode();
  const rename = useInlineRename(node.name, (name) => onRename?.(name));
  const [picked, setPicked] = useState<string>();

  const noText = (content.error as { status?: number } | null)?.status === 404;
  const tabs = tabsFor(node, !noText);
  const tab = tabs.find((t) => t === picked) ?? tabs[0];
  const failed = file?.ingest_status === "failed";
  const needsText = !!tab && !(tab === "original" && kind !== "markdown");
  const text = content.data?.markdown ?? "";

  const fail = (e: Error) => toast.error(e.message);
  const download = () => getKbNodeFile(kbId, node.id).then((blob) => saveBlob(blob, node.name), fail);
  const summary =
    node.description_status === "ready" && node.description ? node.description
      : node.description_status === "pending" && file?.ingest_status === "extracted" && file.tokens ? "Writing a one-line summary…"
      : null;

  return (
    <section aria-label={node.name} className="flex-1 min-w-0 min-h-0 flex flex-col fade-in">
      <header className="shrink-0 px-4 md:px-6 pt-5 space-y-3">
        {/* The actions wrap under the name on a phone rather than squeezing it into a column. */}
        <div className="flex flex-wrap items-start gap-x-3 gap-y-2 min-w-0">
          {onBack && (
            <IconButton size="md" title="Back to the folder" aria-label="Back to the folder" onClick={onBack}>
              <ArrowLeft className="w-4 h-4" strokeWidth={1.75} />
            </IconButton>
          )}
          <FileTypeIcon kind={extOf(node.name) || "file"} size={40} />
          <div className="flex-1 min-w-[180px] basis-[240px]">
            {rename.editing ? (
              <input {...rename.inputProps} className="w-full h-8 px-2 -ml-2 rounded-[8px] bg-[var(--surface)] ring-1 ring-[var(--hairline-focus)] outline-none text-[17px] font-semibold text-[var(--text-primary)]" />
            ) : (
              <h2 className="m-0 text-[17px] font-semibold tracking-[-0.015em] text-[var(--text-primary)] break-words">{node.name}</h2>
            )}
            <div className="mt-1 flex flex-wrap items-center gap-x-1.5 gap-y-1 text-[12.5px] text-[var(--text-tertiary)]">
              {where && <><span className="truncate max-w-[220px]">{where}</span><span className="text-[var(--text-quaternary)]">·</span></>}
              {file && <span className="font-mono tabular-nums">{formatSize(file.bytes)}</span>}
              {file?.pages != null && <><span className="text-[var(--text-quaternary)]">·</span><span>{file.pages} pages</span></>}
              {file?.tokens != null && <><span className="text-[var(--text-quaternary)]">·</span><span className="font-mono tabular-nums">{file.tokens.toLocaleString("en-US")} tokens</span></>}
              <span className="text-[var(--text-quaternary)]">·</span>
              <FileStatusBadge node={node} />
            </div>
          </div>
          <div className="shrink-0 ml-auto flex items-center gap-1.5">
            {failed && (
              <GhostButton disabled={reindex.isPending} onClick={() => reindex.mutate({ kbId, nodeId: node.id }, { onError: fail })}>
                <RefreshCw className="w-3.5 h-3.5" strokeWidth={1.75} /> Reindex
              </GhostButton>
            )}
            <GhostButton onClick={download} aria-label="Download">
              <Download className="w-3.5 h-3.5" strokeWidth={1.75} /> <span className="hidden sm:inline">Download</span>
            </GhostButton>
            {onRename && onMove && onDelete && (
              <KbNodeMenu
                node={node}
                actions={{ onRename: rename.begin, onMove, onDelete, onReindex: () => reindex.mutate({ kbId, nodeId: node.id }, { onError: fail }) }}
              />
            )}
          </div>
        </div>

        {summary && (
          <p className="m-0 flex items-start gap-2 text-[13px] leading-[1.5] text-[var(--text-secondary)]">
            <FileText className="w-3.5 h-3.5 mt-[3px] shrink-0 text-[var(--text-quaternary)]" strokeWidth={1.75} />
            <span className="min-w-0">{summary}</span>
          </p>
        )}
        {failed && (
          <p role="alert" className="m-0 flex items-start gap-2 rounded-[12px] px-3 py-2.5 bg-[var(--danger-bg)] text-[12.5px] leading-[1.5] text-[var(--danger)]">
            <TriangleAlert className="w-3.5 h-3.5 mt-[2px] shrink-0" strokeWidth={1.75} />
            <span>The agent can't read this file{file?.ingest_error ? `: ${file.ingest_error}` : "."} It is kept for viewing and download.</span>
          </p>
        )}
        {kind === "image" && !failed && (
          <p className="m-0 flex items-start gap-2 text-[12.5px] leading-[1.5] text-[var(--text-tertiary)]">
            <ImageIcon className="w-3.5 h-3.5 mt-[2px] shrink-0" strokeWidth={1.75} />
            Agents don't read images. It is kept for viewing and download.
          </p>
        )}

        {tabs.length > 1 && (
          <div className="flex items-center gap-3 pb-3 border-b hairline">
            <div role="tablist" aria-label="Views" className="inline-flex p-[3px] rounded-full bg-[var(--surface-2)]">
              {tabs.map((t) => (
                <button
                  key={t}
                  role="tab"
                  aria-selected={t === tab}
                  onClick={() => setPicked(t)}
                  className={`press whitespace-nowrap px-3 py-[5px] rounded-full text-[12.5px] font-medium ${
                    t === tab
                      ? "bg-[var(--surface)] text-[var(--text-primary)] ring-1 ring-[var(--hairline)] shadow-lift"
                      : "text-[var(--text-tertiary)] hover:text-[var(--text-primary)]"
                  }`}
                >
                  {TAB_LABEL[t]}
                </button>
              ))}
            </div>
            {tab && <span className="hidden sm:block ml-auto text-[12px] text-[var(--text-quaternary)]">{TAB_HINT[tab]}</span>}
          </div>
        )}
      </header>

      <div className="flex-1 min-h-0 flex flex-col px-4 md:px-6 py-4">
        {!tab ? (
          <div className="flex-1 flex flex-col items-center justify-center gap-3 text-center">
            <div className="w-10 h-10 rounded-[12px] bg-[var(--surface-2)] flex items-center justify-center">
              <FileQuestion className="w-[18px] h-[18px] text-[var(--text-tertiary)]" strokeWidth={1.5} />
            </div>
            <div className="text-[14px] font-medium">No text to show</div>
            <div className="text-[12.5px] text-[var(--text-tertiary)] max-w-[320px]">Nothing could be extracted from this file. You can still download it.</div>
          </div>
        ) : needsText && content.isPending ? (
          <Loader2 className="m-auto w-4 h-4 animate-spin text-[var(--text-tertiary)]" />
        ) : needsText && content.error ? (
          <p className="m-auto text-[13px] text-[var(--text-tertiary)]">Could not load the text: {content.error.message}</p>
        ) : tab === "original" ? (
          <div key={tab} className="flex-1 min-h-[360px] rounded-[18px] bg-[var(--surface-2)] p-1 flex">
            <div className="flex-1 min-w-0 flex rounded-[14px] bg-[var(--surface)] ring-1 ring-[var(--hairline)] shadow-lift overflow-hidden">
              <Original kbId={kbId} node={node} text={text} />
            </div>
          </div>
        ) : (
          <div key={tab} className="flex-1 min-h-0 overflow-y-auto">
            {tab === "raw" ? <Raw text={text} /> : <KbIndexed node={node} text={text} />}
          </div>
        )}
      </div>
    </section>
  );
}
